import { isEmpty } from 'lodash';
import { matchSorter } from 'match-sorter';
import moment from 'moment';
import { useMemo, useState } from 'react';

import { messages } from 'i18n';
import { SearchInputProps, Sorters, userFullName } from 'utils';
import { UserMarketSortTypes } from 'broker/components/UserMarkets/utils';
import { ExtendedAgency } from './types';
import { AgenciesSortTypes } from './utils';

export const sorters: Sorters<ExtendedAgency, AgenciesSortTypes> = {
  [UserMarketSortTypes.Updated]: {
    sorter: (a, b) => moment(b.updatedAt).diff(moment(a.updatedAt)),
    label: messages.agenciesPage.sorter.updated,
  },
  [UserMarketSortTypes.Asc]: {
    sorter: (a, b) => a.organizationName.localeCompare(b.organizationName),
    label: messages.agenciesPage.sorter.asc,
  },

  [UserMarketSortTypes.Desc]: {
    sorter: (a, b) => b.organizationName.localeCompare(a.organizationName),
    label: messages.agenciesPage.sorter.desc,
  },
};

export interface UseAgenciesFilterProps extends SearchInputProps {
  sortedAndFilteredItems: ExtendedAgency[];
  sortType: AgenciesSortTypes;
  setSortType: (value: AgenciesSortTypes) => void;
}

export default function useAgenciesFilter(
  agencies: ExtendedAgency[],
  paginationReset: () => void,
  defaultSortType = AgenciesSortTypes.Asc,
): UseAgenciesFilterProps {
  const [sortType, setSortType] = useState<AgenciesSortTypes>(defaultSortType);
  const [searchInput, setSearchInput] = useState<string>('');

  const filteredAgencies = useMemo(
    () =>
      [...agencies]
        .sort(sorters[sortType]?.sorter ?? sorters[defaultSortType].sorter)

        .filter((agency) => {
          if (searchInput.length < 2) {
            return true;
          }

          const userTerms = [
            agency.organizationName,
            ...agency.domains.map((domain) => domain.domain),
            ...agency.agents.flatMap((agent) => [userFullName(agent), agent.email, agent.phoneNumber, agent.title]),
          ].filter(Boolean);
          const isInUserTerms = userTerms.some((term) => term?.toLowerCase().includes(searchInput.toLowerCase()));
          if (isInUserTerms) {
            return isInUserTerms;
          }
          const matchSorterFilter = matchSorter([agency], searchInput, { keys: ['marketName'] });
          return !isEmpty(matchSorterFilter);
        }),
    [agencies, searchInput, sortType, defaultSortType],
  );

  const setSearch = (value: string) => {
    paginationReset();
    setSearchInput(value);
  };

  return {
    search: searchInput,
    setSearch,
    sortType,
    setSortType,
    sortedAndFilteredItems: filteredAgencies,
  };
}
